import moment from 'moment';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { GlobalConfigApiService } from '../../../_core/api/global-config.service';
import { TransactionService } from '../../../_core/api/transaction.service';
import { EnvironmentNames } from '../../../_core/constants/environments';
import FundTypes from '../../../_core/constants/fund-types';
import { getRangePatterns } from '../../../_core/constants/rangePatterns';
import { CustomValidators } from '../../../_core/helpers/custom-validators.helpers';
import SharedMethodsHelpers from '../../../_core/helpers/shared-methods.helpers';
import { Fund } from '../../../_core/models/fund';
import { GrantDataFee } from '../../../_core/models/grantData';
import { CharityProject } from '../../../_core/models/SearchCharity';
import {
  RECURRENCE_PATTERN,
  RECURRENCE_RANGE_END,
} from '../../../_shared/recurring-form/recurring-form.models';
import { createRangeEndValidator } from '../../../_shared/recurring-form/recurring-form.validator';
import { Configuration } from '../../../app.constants';

@Component({
  selector: 'app-grant-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  standalone: false,
})
export class GrantFormComponent implements OnInit, OnChanges {
  @Input() currentFund: Fund;
  @Input() showRecurring = false;
  @Input() preSelectedCharityName: string | undefined;
  @Input() preSelectedProjectName: string | undefined;
  @Input() charityHash: string | undefined;
  @Input() lockCharityAfterSelection = false;
  @Input() displayCurrencySymbol: string | null = null;
  @Input() displayCurrencyCode: string | null = null;
  @Input() grantingCurrencyBalance: number | null = null;
  @Input() formDisabled = false;
  @Input() minimumGrantAmount: number | null = null;

  @Output() formChangedEvent = new EventEmitter<any>();
  @Output() formIsValid = new EventEmitter<boolean>();
  @Output() grantDataFeeEmit = new EventEmitter<GrantDataFee>();

  grantForm: FormGroup;
  recurrenceForm: UntypedFormGroup;
  grantDataFee: GrantDataFee = new GrantDataFee();
  formData: any;
  pageLoading = false;
  calculating = false;
  apiCalculating = false;
  showError = false;
  grantDataMinAmount: number;
  currencyMinAmount: number | null = null;
  zeroDecimalCurrency = false;
  showBalanceWarning = false;

  charities: any[] = [];
  selectedCharityPurpose: string;
  charityProjects: CharityProject[] = [];
  projectSuggestions: CharityProject[] = [];
  loadingProjects = false;

  today: Date;
  charityStartDay: Date;

  fundTypes = FundTypes;
  environmentNames = EnvironmentNames;
  instanceName: string;
  environmentName: string;
  availableAmountToDisplay: number;
  dateFormatPrimeNG = 'mm/dd/yy';
  dateFormatPlaceholder = 'MM/dd/yyyy';
  displayAmount = '';
  decimalSymbol = '.';
  private digitGroupingSymbol = ',';
  private digitGrouping = '123,456,789';

  constructor(
    private fb: FormBuilder,
    private transactionService: TransactionService,
    private config: Configuration,
    private globalConfigApiService: GlobalConfigApiService
  ) {
    this.instanceName = this.config.name;
    this.environmentName = this.config.environmentName;
    this.loadGlobalConfig();
  }

  private loadGlobalConfig(): void {
    this.globalConfigApiService.getConfiguration().subscribe({
      next: (config: any) => {
        if (config?.response?.numberFormat) {
          this.applyFormat(config.response.numberFormat);
        }
        if (config?.response?.dateFormat) {
          this.dateFormatPlaceholder = config.response.dateFormat;
          this.dateFormatPrimeNG = config.response.dateFormat
            .replace(/yyyy/gi, 'yy')
            .replace(/MMMM/g, '\x02')
            .replace(/MMM/g, '\x01')
            .replace(/MM/g, 'mm')
            .replace(/\bM\b/g, 'm')
            .replace(/\x02/g, 'MM')
            .replace(/\x01/g, 'M');
        }
      },
    });
  }

  private applyFormat(numberFormat: any): void {
    this.decimalSymbol = numberFormat.decimalSymbol?.value || '.';
    this.digitGroupingSymbol = numberFormat.digitGroupingSymbol?.value || ',';
    this.digitGrouping = numberFormat.digitGrouping || '123,456,789';
  }

  formatNumberValue(value: number): string {
    const parts = Math.abs(value).toFixed(2).split('.');
    const intPart = parts[0];
    const decPart = parts[1];
    const grouped = this.applyDigitGrouping(intPart);
    const result = grouped + this.decimalSymbol + decPart;
    return value < 0 ? `-${result}` : result;
  }

  private applyDigitGrouping(intPart: string): string {
    const patternParts = this.digitGrouping.replace(/[^0-9,]/g, '').split(',');
    if (patternParts.length <= 1) return intPart;
    const groupSizes = patternParts.map((p) => p.length).reverse();
    const digits = intPart.split('').reverse();
    const groups: string[] = [];
    let pos = 0;
    let sizeIdx = 0;
    while (pos < digits.length) {
      const size = groupSizes[Math.min(sizeIdx, groupSizes.length - 1)];
      groups.push(
        digits
          .slice(pos, pos + size)
          .reverse()
          .join('')
      );
      pos += size;
      sizeIdx++;
    }
    return groups.reverse().join(this.digitGroupingSymbol);
  }

  private parseFormattedValue(formatted: string): number | null {
    if (!formatted || formatted.trim() === '') return null;
    const escaped = this.digitGroupingSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const cleaned = formatted.replace(new RegExp(escaped, 'g'), '');
    const normalized = cleaned.replace(this.decimalSymbol, '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
  }

  onAmountKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];
    if (allowedKeys.includes(event.key)) return;
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())
    )
      return;
    if (/^\d$/.test(event.key)) return;
    if (event.key === this.decimalSymbol) {
      const target = event.target as HTMLInputElement;
      if (target.value.includes(this.decimalSymbol)) event.preventDefault();
      return;
    }
    event.preventDefault();
  }

  onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPos = input.selectionStart || 0;
    const oldValue = input.value;

    const escapedGrouping = this.digitGroupingSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let raw = oldValue.replace(new RegExp(escapedGrouping, 'g'), '');

    const decIdx = raw.indexOf(this.decimalSymbol);
    let intRaw: string;
    let decRaw: string | undefined;

    if (decIdx >= 0) {
      intRaw = raw.substring(0, decIdx).replace(/\D/g, '');
      decRaw = raw
        .substring(decIdx + 1)
        .replace(/\D/g, '')
        .substring(0, 2);
    } else {
      intRaw = raw.replace(/\D/g, '');
    }

    intRaw = intRaw.replace(/^0+(?=\d)/, '');
    const grouped = intRaw ? this.applyDigitGrouping(intRaw) : '';

    let formatted: string;
    if (decIdx >= 0) {
      formatted = grouped + this.decimalSymbol + (decRaw ?? '');
    } else {
      formatted = grouped;
    }

    let digitsBefore = 0;
    for (let i = 0; i < cursorPos && i < oldValue.length; i++) {
      if (oldValue[i] !== this.digitGroupingSymbol) digitsBefore++;
    }

    let newPos = 0;
    let count = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== this.digitGroupingSymbol) count++;
      if (count === digitsBefore) {
        newPos = i + 1;
        break;
      }
    }
    if (count < digitsBefore) newPos = formatted.length;

    let parsed = this.parseFormattedValue(formatted);

    // // Enforce max value
    // if (
    //   parsed !== null &&
    //   this.availableAmountToDisplay &&
    //   parsed > this.availableAmountToDisplay
    // ) {
    //   parsed = this.availableAmountToDisplay;
    //   formatted = this.formatNumberValue(parsed);
    //   newPos = formatted.length;
    // }

    input.value = formatted;
    input.setSelectionRange(newPos, newPos);
    this.displayAmount = formatted;
    this.showError = false;
    this.calculating = true;
    this.amount.patchValue(parsed, { emitEvent: true });
  }

  onAmountBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.amount.markAsTouched();
    let parsed = this.parseFormattedValue(input.value);

    // // Enforce max value
    // if (
    //   parsed !== null &&
    //   this.availableAmountToDisplay &&
    //   parsed > this.availableAmountToDisplay
    // ) {
    //   parsed = this.availableAmountToDisplay;
    // }

    this.amount.patchValue(parsed, { emitEvent: true });
    if (parsed !== null) {
      this.displayAmount = this.formatNumberValue(parsed);
      input.value = this.displayAmount;
    } else {
      this.displayAmount = '';
    }
    this.checkAmount();
  }

  ngOnInit(): void {
    if (!this.currentFund) return;
    this.today = moment().startOf('day').toDate();
    this.charityStartDay = moment().startOf('day').add(7, 'days').toDate();
    this.computeAvailableAmountToDisplay();
    this.createForm();
    if (this.formDisabled) {
      this.grantForm.disable({ emitEvent: false });
    }
    this.setFundName();
    this.grantForm.valueChanges.pipe(debounceTime(400)).subscribe(() => this.emitChanges());
    this.recurrenceForm.valueChanges.pipe(debounceTime(400)).subscribe(() => {
      this.emitChanges();
      this._updateGrantWarning();
    });
    this.loadCurrencyMinimumAmount();
    if (this.currentFund?.fundType === FundTypes.DAF) {
      if (this.charityHash) {
        this.pageLoading = true;
        this.transactionService.getFundNumberFromHash(this.charityHash).subscribe({
          next: (response: any) => {
            const fundNumber = response?.fundNumber;
            if (fundNumber) {
              this.searchAndAutoSelectCharity(fundNumber, this.lockCharityAfterSelection);
            } else if (this.preSelectedCharityName) {
              this.searchAndAutoSelectCharity(this.preSelectedCharityName, this.lockCharityAfterSelection);
            } else {
              this.pageLoading = false;
            }
          },
          error: () => {
            if (this.preSelectedCharityName) {
              this.searchAndAutoSelectCharity(this.preSelectedCharityName, this.lockCharityAfterSelection);
            } else {
              this.pageLoading = false;
            }
          },
        });
      } else if (this.preSelectedCharityName) {
        this.pageLoading = true;
        this.searchAndAutoSelectCharity(this.preSelectedCharityName, this.lockCharityAfterSelection);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentFund']) {
      this.currentFund = changes['currentFund'].currentValue;
      if (this.currentFund && !this.grantForm) {
        this.today = moment().startOf('day').toDate();
        this.charityStartDay = moment().startOf('day').add(7, 'days').toDate();
        this.computeAvailableAmountToDisplay();
        this.createForm();
        this.setFundName();
        this.grantForm.valueChanges.pipe(debounceTime(400)).subscribe(() => this.emitChanges());
        this.recurrenceForm.valueChanges.pipe(debounceTime(400)).subscribe(() => {
          this.emitChanges();
          this._updateGrantWarning();
        });
        this.loadCurrencyMinimumAmount();
      } else if (this.currentFund && this.grantForm) {
        this.setFundName();
        this.currencyMinAmount = null;
        this.zeroDecimalCurrency = false;
        this.loadCurrencyMinimumAmount();
      }
    }
    if (changes['displayCurrencySymbol'] && this.grantForm) {
      this.loadCurrencyMinimumAmount();
    }
    if (changes['grantingCurrencyBalance'] && this.grantForm) {
      this.computeAvailableAmountToDisplay();
      this.amount.setValidators([
        Validators.required,
        CustomValidators.maxAmountAllowed(this.availableAmountToDisplay),
      ]);
      this.amount.updateValueAndValidity();
      this._updateGrantWarning();
    }
    if (changes['formDisabled'] && this.grantForm) {
      if (this.formDisabled) {
        this.grantForm.disable({ emitEvent: false });
      } else {
        this.grantForm.enable({ emitEvent: false });
        this.setFundName(); // restore controls disabled by business rules (e.g. CHARITY receiver)
      }
    }
    if (this.currentFund?.fundType === FundTypes.PROJECT && !this.pageLoading) {
      this.pageLoading = true;
      const fundNumber = this.currentFund.fundNumber.split(':')[0]?.trim();
      this.searchAndAutoSelectCharity(fundNumber);
    }
  }

  searchCharities(event: any): void {
    const query = event.query;
    this.transactionService
      .getCharityByString({ countryName: 'other', charityNameOrNumber: query, taxDeduction: false })
      .subscribe((results: any) => {
        this.charities = (results || []).map((c: any) => ({
          ...c,
          displayName: `${c.fundName} (#${c.fundNumber})`,
        }));
      });
  }

  private searchAndAutoSelectCharity(fundNumber: string, disableAfterSelect = true): void {
    this.transactionService
      .getCharityByString({
        countryName: 'other',
        charityNameOrNumber: fundNumber,
        taxDeduction: false,
      })
      .subscribe((results: any) => {
        if (results && results.length > 0) {
          const charity = {
            ...results[0],
            displayName: `${results[0].fundName} (#${results[0].fundNumber})`,
          };
          this.receiverFundNameAndNumber.setValue(charity);
          if (disableAfterSelect) {
            this.receiverFundNameAndNumber.disable();
          }
          this.selectedCharityPurpose = results[0].cseg6Text;

          if (results[0].cseg5) {
            this.loadingProjects = true;
            this.transactionService.getCharityProjects(results[0].cseg5).subscribe({
              next: (projects) => {
                this.charityProjects = (projects || [])
                  .filter((p) => p.status === 'Project Application Approved')
                  .map((p) => ({ ...p, displayName: `${p.entityName} (#${p.fundNumber})` }));
                this.projectSuggestions = [...this.charityProjects];

                const match = this.charityProjects.find(
                  (p) =>
                    p.entityName?.toLowerCase() === this.preSelectedProjectName?.toLowerCase() ||
                    p.fundName?.toLowerCase() === this.preSelectedProjectName?.toLowerCase() ||
                    p.entityName?.toLowerCase() === `${this.preSelectedProjectName?.toLowerCase()} - ${this.preSelectedCharityName?.toLowerCase()}`
                );
                if (match) {
                  this.charityProjectControl?.setValue(match);
                  if (this.preSelectedProjectName) {
                    this.charityProjectControl?.disable();
                  }
                }
                this.loadingProjects = false;
              },
              error: () => {
                this.loadingProjects = false;
              },
            });
          }
        }
        this.pageLoading = false;
      });
  }

  createForm(): void {
    this.grantForm = this.fb.group({
      receiverFundName: [null, [Validators.required]],
      charityProject: [null],
      amount: [
        null,
        [Validators.required, CustomValidators.maxAmountAllowed(this.availableAmountToDisplay)],
      ],
      privacy: [false],
      timing: ['', [Validators.required]],
      date: [''],
      showCharityInstruction: [false],
      charityInstruction: [''],
      showAdditionalNotes: [false],
      additionalNotes: [''],
    });

    this.recurrenceForm = this.fb.group({
      recurrencePattern: [RECURRENCE_PATTERN.WEEKLY],
      recurrenceRange: this.fb.group(
        {
          startDate: [moment(new Date()).valueOf()],
          rangeOption: [null],
          endDate: [null],
          rangeOccurrences: [2],
        },
        { validators: createRangeEndValidator() }
      ),
    });

    this.timing.valueChanges.subscribe((value) => this.handleTimingChange(value));

    this.showCharityInstruction.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value) => this.handleChange(value, this.charityInstruction));

    this.showAdditionalNotes.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value) => this.handleChange(value, this.additionalNotes));

    this.amount.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      this.calculating = true;
      this.showError = false;

      const errors = { ...this.amount.errors };
      delete errors['minAmount'];
      delete errors['noDecimals'];

      if (value !== null && this.zeroDecimalCurrency && !Number.isInteger(value)) {
        errors['noDecimals'] = true;
      }
      //minimum grant amount validation for DAF
      else if (value !== null && this.minimumGrantAmount && value < this.minimumGrantAmount
                && this.currentFund.fundType === FundTypes.DAF) {
        this.grantDataMinAmount = this.minimumGrantAmount;
        this.showError = true;
      } else {
        this.grantDataMinAmount = null;
        this.showError = false;
      }

      this.amount.setErrors(Object.keys(errors).length ? errors : null, { emitEvent: false });
      this.calculating = false;
      this.emitChanges();
      this._updateGrantWarning();
      this.checkAmount();
    });
  }

  setFundName(): void {
    if (!this.grantForm) return;
    if (this.currentFund.fundType === FundTypes.CHARITY) {
      const charityObj = {
        ...this.currentFund,
        displayName: `${this.currentFund.fundName} (#${this.currentFund.fundNumber})`,
      };
      this.receiverFundNameAndNumber.setValue(charityObj);
      this.receiverFundNameAndNumber.disable();
    } else {
      this.receiverFundNameAndNumber.setValue(null);
      this.receiverFundNameAndNumber.enable();
    }
  }

  handleTimingChange(value: string): void {
    if (value === 'specificDate') {
      this.date.setValidators([Validators.required]);
    } else {
      this.date.patchValue('');
      this.date.clearValidators();
    }
    this.date.updateValueAndValidity();
    this._updateGrantWarning();
  }

  handleChange(value: boolean, abstractControl: AbstractControl): void {
    if (value) {
      abstractControl.setValidators([
        Validators.required,
        CustomValidators.minLength(10),
        CustomValidators.maxLength(250),
      ]);
    } else {
      abstractControl.patchValue('');
      abstractControl.clearValidators();
    }
    abstractControl.updateValueAndValidity();
  }

  onSelectCharity(event: any): void {
    this.selectedCharityPurpose = event.value?.cseg6Text;
    this.charityProjectControl?.setValue(null);
    this.charityProjects = [];
    this.projectSuggestions = [];
    const cseg5 = event.value?.cseg5;
    if (cseg5) {
      this.loadingProjects = true;
      this.transactionService.getCharityProjects(cseg5).subscribe({
        next: (projects) => {
          this.charityProjects = (projects || [])
            .filter((p) => p.status === 'Project Application Approved')
            .map((p) => ({ ...p, displayName: `${p.entityName} (#${p.fundNumber})` }));
          this.projectSuggestions = [...this.charityProjects];
          this.loadingProjects = false;
        },
        error: () => {
          this.loadingProjects = false;
        },
      });
    }
    this.emitChanges();
  }

  onClearCharity(): void {
    this.selectedCharityPurpose = null;
    this.charityProjects = [];
    this.projectSuggestions = [];
    this.charityProjectControl?.setValue(null);
    this.emitChanges();
  }

  searchProjects(event: any): void {
    const query = (event.query || '').toLowerCase();
    this.projectSuggestions = this.charityProjects.filter(
      (p) =>
        p.fundName.toLowerCase().includes(query) ||
        p.fundNumber.toLowerCase().includes(query) ||
        (p.entityName || '').toLowerCase().includes(query)
    );
  }

  onClearProject(): void {
    this.emitChanges();
  }

  checkAmount(): void {
    this.computeFormData();
    if (this.amount.value === null) return;
    const errors = this.amount.errors ?? {};
    const onlyMinAmountError = Object.keys(errors).length === 1 && !!errors['minAmount'];
    if (this.amount.invalid && !onlyMinAmountError) return;

    if (this.currentFund.fundType === FundTypes.CHARITY) {
      this.apiCalculating = true;
      this.showError = false;
      this.grantDataMinAmount = null;
      const payload = this.buildCalculateGrantPayload();
      this.transactionService.calculateGrant(payload).subscribe({
        next: (data: GrantDataFee) => {
          this.apiCalculating = false;
          this.grantDataFee = data;
          this.clearMinAmountError();
          this.emitChanges();
        },
        error: (error) => {
          this.apiCalculating = false;
          this.showError = true;
          this.emitChanges();
          if (error.status === 409) {
            this.grantDataMinAmount = error.error.minimumGrantAmount;
            this.clearMinAmountError();
          }
        },
      });
    }
  }

  computeAvailableAmountToDisplay(): void {
    const balance = this.grantingCurrencyBalance ?? this.currentFund.availableAmount;
    if (balance) {
      this.availableAmountToDisplay = SharedMethodsHelpers.computeAmountToDisplay(
        balance.toString()
      );
    } else {
      this.availableAmountToDisplay = 0;
    }
  }

  emitChanges(): void {
    this.computeFormData();
    const charityValue = this.receiverFundNameAndNumber.value;
    const isCharityValid = !!charityValue && typeof charityValue === 'object';
    const recurringValid = !this.isRecurring || (this.recurrenceForm?.valid ?? false);
    const isValid =
      isCharityValid &&
      this.grantForm.valid &&
      !this.showError &&
      !this.calculating &&
      recurringValid;
    this.formIsValid.emit(isValid);
    this.formChangedEvent.emit(this.formData);
    if (this.grantDataFee) this.grantDataFeeEmit.emit(this.grantDataFee);
  }

  computeFormData(): void {
    const charity = this.receiverFundNameAndNumber.value;
    const receiverCseg5 = charity?.cseg5 || null;
    const receiverFundName = charity?.fundName || null;
    const receiverFundNumber = charity?.fundNumber || null;
    const isRecurring = this.isRecurring;
    const recurringExtra = isRecurring
      ? {
          recurrencePattern: this.recurrenceForm?.get('recurrencePattern')?.value ?? null,
          startDate: this.recurrenceForm?.get('recurrenceRange.startDate')?.value ?? null,
          endDate: this.recurrenceForm?.get('recurrenceRange.endDate')?.value ?? null,
        }
      : {};
    const project = this.charityProjectControl?.value;
    const charityProjectCseg5 = project?.cseg5 || null;
    const charityProjectName = project ? `${project.entityName} (#${project.fundNumber})` : null;
    const charityProjectNameOnly = project ? project.entityName : null;

    if (this.currentFund.fundType === FundTypes.CHARITY) {
      this.formData = {
        ...this.grantForm.getRawValue(),
        ...this.grantDataFee,
        receiverCseg5,
        receiverFundName,
        receiverFundNumber,
        charityProjectCseg5,
        charityProjectName,
        charityProjectNameOnly,
        ...recurringExtra,
      };
    } else {
      this.formData = {
        ...this.grantForm.getRawValue(),
        receiverCseg5,
        receiverFundName,
        receiverFundNumber,
        charityProjectCseg5,
        charityProjectName,
        charityProjectNameOnly,
        ...recurringExtra,
      };
    }
  }

  isReceiverEqualWithSenderFund(): boolean {
    const charity = this.receiverFundNameAndNumber?.value;
    return !!(
      charity &&
      typeof charity === 'object' &&
      this.currentFund.fundName === charity.fundName
    );
  }

  availableAmountFloor(): number {
    return SharedMethodsHelpers.floor(this.currentFund.availableAmount);
  }

  private clearMinAmountError(): void {
    const errs = { ...this.amount.errors };
    delete errs['minAmount'];
    this.amount.setErrors(Object.keys(errs).length ? errs : null, { emitEvent: false });
  }

  private loadCurrencyMinimumAmount(): void {
    this.transactionService.checkMinimumAmount(this.displayCurrencySymbol || this.currentFund.currencySymbol).subscribe({
      next: (res: any) => {
        this.currencyMinAmount = res?.value ? parseFloat(res.value) : null;
        this.zeroDecimalCurrency = res?.zeroDecimalCurrency ?? false;
      },
      error: () => {},
    });
  }

  private buildCalculateGrantPayload(): any {
    return {
      amount: this.amount.value,
      currency: this.displayCurrencyCode || this.currentFund.currency,
      senderFundId: this.currentFund.cseg5,
    };
  }

  private _updateGrantWarning(): void {
    if (!this.recurrenceForm || !this.isRecurring) {
      this.showBalanceWarning = false;
      return;
    }
    const available = this.availableAmountToDisplay;
    const amount = this.amount.value ?? 0;
    const rangeOption = this.recurrenceForm.get('recurrenceRange.rangeOption')
      ?.value as RECURRENCE_RANGE_END;
    const pattern = this.recurrenceForm.get('recurrencePattern')?.value as RECURRENCE_PATTERN;
    const occurrences = this.recurrenceForm.get('recurrenceRange.rangeOccurrences')?.value ?? 2;
    const startDate = this.recurrenceForm.get('recurrenceRange.startDate')?.value;
    const endDate = this.recurrenceForm.get('recurrenceRange.endDate')?.value;

    if (rangeOption === RECURRENCE_RANGE_END.NOEND) {
      this.showBalanceWarning = true;
      return;
    }
    if (rangeOption === RECURRENCE_RANGE_END.ENDAFTER) {
      this.showBalanceWarning = amount * occurrences > available;
      return;
    }
    if (rangeOption === RECURRENCE_RANGE_END.ENDBY && endDate) {
      const patterns = getRangePatterns(startDate, new Date(endDate).getTime());
      this.showBalanceWarning = amount * (patterns[pattern] ?? 1) > available;
      return;
    }
    this.showBalanceWarning = false;
  }

  get receiverFundNameAndNumber(): AbstractControl {
    return this.grantForm.get('receiverFundName');
  }

  get charityProjectControl(): AbstractControl {
    return this.grantForm.get('charityProject');
  }

  get amount(): AbstractControl {
    return this.grantForm.get('amount');
  }

  get timing(): AbstractControl {
    return this.grantForm.get('timing');
  }

  get date(): AbstractControl {
    return this.grantForm.get('date');
  }

  get showCharityInstruction(): AbstractControl {
    return this.grantForm.get('showCharityInstruction');
  }

  get charityInstruction(): AbstractControl {
    return this.grantForm.get('charityInstruction');
  }

  get showAdditionalNotes(): AbstractControl {
    return this.grantForm.get('showAdditionalNotes');
  }

  get additionalNotes(): AbstractControl {
    return this.grantForm.get('additionalNotes');
  }

  get isRecurring(): boolean {
    return this.timing?.value === 'recurring';
  }

  get shouldHideProjectField(): boolean {
    return this.lockCharityAfterSelection && !this.preSelectedProjectName;
  }
}
